import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ArrowUpRight } from 'lucide-react';
import { useRouter } from '../RouterContext';

const VULPES_LOGO = 'https://cadernos.vulpes.pt/static/media/VULPES_SIMBOLO_V1_512.3e79a125fe0e79e6f5cc.png';
const AXOLOTE_LOGO = '/Axolote_n.png';

const products = [
  {
    name: 'Vulpes',
    logo: VULPES_LOGO,
    badge: 'AgriTech · Energia · Automação',
    tagline: 'Tecnologia que simplifica o quotidiano agrícola e gera resultados mensuráveis.',
    body: 'A Vulpes combina inteligência artificial, automação e interoperabilidade para oferecer soluções de gestão de rega, cadernos de campo digitais e eficiência energética — tudo numa plataforma orientada para a sustentabilidade.',
    features: [
      'Gestão inteligente de rega',
      'Cadernos de campo digitais',
      'Monitorização energética',
      'IA e automação de processos',
    ],
    img: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=900&q=85&auto=format&fit=crop&crop=center',
    accent: '#00C896',
    url: 'https://vulpes.pt/',
    page: null as null,
    reverse: true,
  },
  {
    name: 'Axolote',
    logo: AXOLOTE_LOGO,
    badge: 'Inteligência Artificial · Agentes · Previsão',
    tagline: 'A plataforma de agentes de IA para automatizar e antecipar o futuro do seu negócio.',
    body: 'A Axolote é uma plataforma de inteligência artificial baseada em agentes, desenvolvida para ajudar empresas a automatizar processos críticos e a prever receitas e produção com maior precisão. Os agentes integram-se facilmente com os sistemas existentes, identificam padrões e optimizam recursos.',
    features: [
      'Agentes autónomos de IA',
      'Previsão de receitas e produção',
      'Automação de processos operacionais',
      'Integração com sistemas existentes',
    ],
    img: 'https://images.unsplash.com/photo-1531746790731-6c087fecd65a?w=900&q=85&auto=format&fit=crop&crop=center',
    accent: '#7C3AED',
    url: null as null,
    page: 'axolote' as const,
    reverse: false,
  },
  {
    name: 'Logitrax',
    logo: null as null,
    badge: 'Indústria · Logística · IA',
    tagline: 'Gestão inteligente de armazéns e produção com inteligência artificial.',
    body: 'O Logitrax integra em tempo real dados de stock, logística e produção, permitindo controlo total sobre toda a operação — desde a entrada de matérias-primas até à expedição do produto final.',
    features: [
      'Planeamento dinâmico de produção',
      'Gestão de stock em tempo real',
      'Previsão de necessidades com IA',
      'Controlo de expedição e logística',
    ],
    img: 'https://images.unsplash.com/photo-1553413077-190dd305871c?w=900&q=85&auto=format&fit=crop&crop=center',
    accent: '#0057FF',
    url: null as null,
    page: 'logitrax' as const,
    reverse: true,
  },
];

const Products: React.FC = () => {
  const { navigate } = useRouter();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.08 });

  return (
    <section id="products" style={s.section} ref={ref}>

      {/* Header */}
      <div style={s.header}>
        <motion.div
          style={s.headerInner}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <span style={s.label}>Produtos</span>
          <h2 style={s.title}>Soluções próprias no mercado</h2>
          <p style={s.subtitle}>
            Para além dos serviços de consultoria, desenvolvemos produtos tecnológicos
            com identidade própria, prontos a implementar.
          </p>
        </motion.div>
      </div>

      {/* Product rows */}
      {products.map((p, i) => (
        <div key={i} style={{ ...s.row, borderTop: '1px solid #E8EDF3' }}>
          <div style={{ ...s.split, flexDirection: p.reverse ? 'row-reverse' : 'row' }}>

            {/* Text */}
            <motion.div
              style={s.textCol}
              initial={{ opacity: 0, y: 24 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.65, delay: 0.1 + i * 0.1 }}
            >
              <span style={s.badge}>{p.badge}</span>

              <div style={s.nameRow}>
                {p.logo && <img src={p.logo} alt={p.name} style={s.logo} />}
                <h3 style={s.productName}>{p.name}</h3>
              </div>

              <p style={s.tagline}>{p.tagline}</p>
              <p style={s.body}>{p.body}</p>

              <div style={s.featureList}>
                {p.features.map((f, j) => (
                  <div key={j} style={s.feature}>
                    <span style={{ ...s.featureDot, background: p.accent }} />
                    {f}
                  </div>
                ))}
              </div>

              {p.url ? (
                <a
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ ...s.cta, color: p.accent, borderBottomColor: p.accent }}
                >
                  Visitar site <ArrowUpRight size={15} />
                </a>
              ) : (
                <button
                  onClick={() => navigate(p.page!)}
                  style={{ ...s.cta, color: p.accent, borderBottomColor: p.accent }}
                >
                  Saber mais <ArrowUpRight size={15} />
                </button>
              )}
            </motion.div>

            {/* Image */}
            <motion.div
              style={s.imgCol}
              initial={{ opacity: 0, y: 24 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.65, delay: 0.2 + i * 0.1 }}
            >
              <div style={s.imgWrap}>
                <img src={p.img} alt={p.name} style={s.img} />
              </div>
            </motion.div>

          </div>
        </div>
      ))}

    </section>
  );
};

const s: { [key: string]: React.CSSProperties } = {
  section: {
    background: '#ffffff',
    borderTop: '1px solid #E8EDF3',
  },
  header: {
    padding: '100px 0 60px',
  },
  headerInner: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 2rem',
  },
  label: {
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '0.7rem',
    fontWeight: 600,
    letterSpacing: '0.25em',
    textTransform: 'uppercase',
    color: '#00C896',
    display: 'block',
    marginBottom: '1rem',
  },
  title: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: 'clamp(1.75rem, 3vw, 2.5rem)',
    fontWeight: 800,
    color: '#0D1B2A',
    letterSpacing: '-0.02em',
    marginBottom: '0.75rem',
    lineHeight: 1.2,
  },
  subtitle: {
    fontSize: '1rem',
    color: '#64748B',
    lineHeight: 1.7,
    maxWidth: '520px',
    margin: 0,
  },
  row: {
    background: '#ffffff',
  },
  split: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 2rem',
    display: 'flex',
    gap: '5rem',
    alignItems: 'center',
    paddingTop: '80px',
    paddingBottom: '80px',
  },
  textCol: {
    flex: 1,
  },
  badge: {
    display: 'inline-block',
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '0.7rem',
    fontWeight: 600,
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    color: '#64748B',
    background: '#F1F5F9',
    padding: '0.3rem 0.8rem',
    marginBottom: '1.25rem',
  },
  nameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    marginBottom: '0.75rem',
  },
  logo: {
    width: '52px',
    height: '52px',
    objectFit: 'contain',
  },
  productName: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: 'clamp(2.5rem, 5vw, 4rem)',
    fontWeight: 900,
    color: '#0D1B2A',
    letterSpacing: '-0.03em',
    lineHeight: 1.05,
    margin: 0,
  },
  tagline: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: '1.05rem',
    fontWeight: 600,
    color: '#334155',
    lineHeight: 1.5,
    marginBottom: '1rem',
  },
  body: {
    fontSize: '0.95rem',
    color: '#64748B',
    lineHeight: 1.85,
    marginBottom: '1.5rem',
  },
  featureList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.55rem',
    marginBottom: '1.75rem',
    paddingTop: '1rem',
    borderTop: '1px solid #E8EDF3',
  },
  feature: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.65rem',
    fontSize: '0.875rem',
    color: '#334155',
    fontWeight: 500,
  },
  featureDot: {
    width: '5px',
    height: '5px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  cta: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.4rem',
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '0.8rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    textDecoration: 'none',
    background: 'none',
    border: 'none',
    borderBottom: '1px solid',
    paddingBottom: '2px',
    marginTop: '0.25rem',
    cursor: 'pointer',
  },
  imgCol: {
    flex: 1,
    lineHeight: 0,
    overflow: 'hidden',
  },
  imgWrap: {
    overflow: 'hidden',
    lineHeight: 0,
  },
  img: {
    width: '100%',
    height: '420px',
    objectFit: 'cover',
    display: 'block',
    filter: 'grayscale(15%)',
  },
};

export default Products;
